import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import pako from 'pako';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Legend, Treemap } from 'recharts';
import { gwasData } from './data/gwasData';
import { finnGenData } from './data/finnGenData';

import img1 from './assets/img1.png';
import img2 from './assets/img2.png';
import img3 from './assets/img3.png';
import img4 from './assets/img4.png';
import img5 from './assets/img5.png';
import img6 from './assets/img6.png';
import img7 from './assets/img7.png';
import overviewImg from './assets/Overview.png';

// Parse CSV with proper handling of quoted fields containing commas
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote (double quote)
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }
  
  result.push(current);
  return result;
}

interface Feature {
  id: number;
  title: string;
  description: string;
  image: string;
}

const features: Feature[] = [
  { id: 1, title: 'Genetic Correlation', description: 'Using summary statistics and corresponding reference panel, CREGPnet defines the genetic correlation between two interested traits.', image: '/Help_pic/method1_ldsc.png' },
  { id: 2, title: 'Mendelian Randomization', description: 'Based on selected instrumental variables (IVs), CREGPnet introduces TwoSampleMR to define the causal relationship between exposure (Trait 1) and outcome (Trait 2).', image: '/Help_pic/method2_mr.svg' },
  { id: 3, title: 'Colocalization in LD Block', description: 'The whole genome is split into some LD blocks. Define the pleiotropy for the two traits in each block.', image: '/Help_pic/method3_coloc.svg' },
  { id: 4, title: 'Summary Mendelian Randomization', description: 'Using the MR assumption, SMR defines the causal gene by the selected IVs. CREGPnet intersects the causal genes for traits.', image: '/Help_pic/method4_smr.svg' },
  { id: 5, title: 'Overall Causal Effect for Gene', description: 'IVW estimate aggregating multiple cis‑eQTL instruments to test the overall causal effect of predicted gene expression on the trait.', image: '/Help_pic/method5_gene.svg' },
  { id: 6, title: 'Phenotypic co-occurrence', description: 'INTACT complements the result of SMR and coloc.', image: '/Help_pic/method6_phenotypic.svg' },
  { id: 7, title: 'Disease Causal Network Identification', description: 'Identifying causal regulatory networks of phenotypes based on BN-GWAS method, integrating cis-regulatory elements (CRE), genes and phenotypes.', image: '/Help_pic/method7_bngwas.svg' }
];

const databaseOutputs = [
  'Phenotype Genetic Correlation', 'GWAS Colocalization', 'Mendelian Randomization', 'HyPrColoc Causal Colocalization',
  'Phenotype Regulatory Network', 'CRE Target Gene Association', 'ENCODE Regulatory Annotation', 'Gene Clinical Phenotype'
];

// Home Page Component
function HomePage() {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Process data for subcategory distribution by category
  const subcategoryData = useMemo(() => {
    const data: Record<string, { name: string; value: number }[]> = {};
    const categories = ['Disease', 'Measurement', 'Drug', 'Behavior'];
    
    categories.forEach(cat => {
      const items = finnGenData.filter(item => item.phenotypicCategory.toLowerCase() === cat.toLowerCase());
      const subcatCounts: Record<string, number> = {};
      items.forEach(item => {
        const subcat = item.phenotypicSubcategory;
        subcatCounts[subcat] = (subcatCounts[subcat] || 0) + 1;
      });
      data[cat] = Object.entries(subcatCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    });
    return data;
  }, []);

  const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ec4899', '#14b8a6', '#6366f1'];

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" />
                <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-lg font-bold text-gray-800 hidden sm:block">CREGPnet</span>
          </div>
          <div className="h-6 w-px bg-gray-200 flex-shrink-0 hidden md:block" />
          <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0">
            {[
              { id: 1, short: 'LDSC' },
              { id: 2, short: 'MR' },
              { id: 3, short: 'Coloc' },
              { id: 4, short: 'SMR' },
              { id: 5, short: 'Gene Causal' },
              { id: 6, short: 'Phenotypic' },
              { id: 7, short: 'BN-GWAS' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id <= 4) {
                    const el = document.getElementById(`feature-${item.id}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else if (item.id === 5) {
                    navigate('/gene-causal');
                  } else if (item.id === 6) {
                    const el = document.getElementById(`feature-${item.id}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else if (item.id === 7) {
                    const el = document.getElementById(`feature-${item.id}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#3b82f6] hover:bg-blue-50 rounded-md transition-colors whitespace-nowrap flex-shrink-0"
              >
                {item.short}
              </button>
            ))}
          </div>
          <div className="h-6 w-px bg-gray-200 flex-shrink-0" />
          <button
            onClick={() => navigate('/help')}
            className="px-4 py-1.5 text-xs font-medium text-white bg-[#3b82f6] hover:bg-[#2563eb] rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Help
          </button>
        </div>
      </nav>
      <header className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-6"
          style={{ backgroundImage: `url(https://public.youware.com/users-website-assets/prod/479e0d88-c718-4735-8d11-f793020956b3/358e873489cd4aa5acb9cd2d27d1f8f9.jpg)` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8f7f4] via-[#f8f7f4]/90 to-[#f8f7f4]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-40 h-40 border border-[#3b82f6]/15 rounded-full" />
          <div className="absolute bottom-40 right-20 w-60 h-60 border border-[#8b5cf6]/15 rounded-full" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center shadow-xl shadow-[#3b82f6]/25">
              <svg className="w-9 h-9 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" />
                <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">CREGPnet</h1>
          <p className="text-gray-600 text-lg mb-2">Comprehensive Causal Regulatory Network Database</p>
          <p className="text-gray-500 text-base mb-4">Integrating cis-regulatory elements (CRE), genes and phenotypes to build complete causal regulatory networks</p>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="text-center"><div className="text-2xl font-bold text-[#3b82f6]">6,504</div><div className="text-xs text-gray-500">GWAS Studies</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-[#8b5cf6]">4,675</div><div className="text-xs text-gray-500">Disease Phenotypes</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-[#3b82f6]">677</div><div className="text-xs text-gray-500">Measurement Phenotypes</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-[#8b5cf6]">678</div><div className="text-xs text-gray-500">Drug Phenotypes</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-[#3b82f6]">474</div><div className="text-xs text-gray-500">Behavior Phenotypes</div></div>
          </div>
        </div>
      </header>
      {/* Overview Section */}
      <section className="py-6 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Left: Overview Image */}
              <div className="md:w-[55%] flex-shrink-0">
                <img src={overviewImg} alt="CREGPnet Overview" className="w-full h-auto rounded-lg border border-gray-100" />
              </div>
              {/* Right: Database Description */}
              <div className="md:w-[45%] space-y-4">
                <h2 className="text-xl font-bold text-gray-800">CREGPnet Database Overview</h2>
                <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                  <p
                    style={{
                      fontSize: "13px",
                      fontWeight: "normal"
                    }}>
                    CREGPnet is a comprehensive causal regulatory network database designed to systematically bridge the mechanistic gap between non-coding genetic variants and complex phenotypes. By integrating large-scale GWAS resources, cis-regulatory elements (CREs), genes, and multi-level omics annotations, CREGPnet establishes a unified "SNP–CRE–Gene–Phenotype" causal framework for interpreting complex trait genetics. The database incorporates over 125,000 publicly available GWAS projects from resources including GWAS Catalog, UK Biobank, FinnGen, and NHGRI-EBI, followed by stringent ancestry, sample-size, SNP-quality, and heritability filtering to retain 6,504 high-quality GWAS datasets covering disease, measurement, drug-response, and behavioral phenotypes.
                  </p>
                  <p
                    style={{
                      fontSize: "13px"
                    }}>
                    To infer causal regulatory relationships rather than simple statistical associations, CREGPnet integrates multiple complementary analytical strategies, including LDSC, COLOC, Mendelian randomization (MR), HyPrColoc, MAGMA, and BN-GWAS. Regulatory interactions between CREs and genes are further refined through integration of eQTL, Hi-C, ENCODE, and dbSNP annotations, enabling accurate mapping of non-coding GWAS signals onto functional regulatory elements and target genes. Through Bayesian network–based causal modeling, CREGPnet reconstructs interpretable CRE–gene–phenotype regulatory circuits and generates phenotype–phenotype, disease–drug, and causal regulatory networks.
                  </p>
                  <p
                    style={{
                      fontSize: "13px"
                    }}>
                    By combining large-scale standardized GWAS analysis with systematic non-coding regulatory inference, CREGPnet provides a powerful resource for functional interpretation of GWAS loci, discovery of core regulatory pathways, identification of therapeutic targets, and mechanistic understanding of complex human diseases.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Phenotype Classification Section */}
      <section className="py-5 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-gray-800 text-center mb-6">Phenotype Classification</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-5 rounded-xl border hover:shadow-lg transition-all text-left ${selectedCategory === 'Disease' ? 'bg-red-50 border-red-300 ring-2 ring-red-200' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </div>
                <button onClick={() => setSelectedCategory(selectedCategory === 'Disease' ? null : 'Disease')} className="text-lg font-bold text-gray-800 hover:text-red-600">Disease</button>
              </div>
              <button onClick={() => navigate('/gwas/disease')} className="text-3xl font-bold text-red-600 hover:underline">4,675</button>
            </div>
            
            <div className={`p-5 rounded-xl border hover:shadow-lg transition-all text-left ${selectedCategory === 'Measurement' ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                </div>
                <button onClick={() => setSelectedCategory(selectedCategory === 'Measurement' ? null : 'Measurement')} className="text-lg font-bold text-gray-800 hover:text-blue-600">Measurement</button>
              </div>
              <button onClick={() => navigate('/gwas/measurement')} className="text-3xl font-bold text-blue-600 hover:underline">677</button>
            </div>
            
            <div className={`p-5 rounded-xl border hover:shadow-lg transition-all text-left ${selectedCategory === 'Drug' ? 'bg-green-50 border-green-300 ring-2 ring-green-200' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.414 1.414.586 3.414-1.414 3.414H12a2 2 0 01-2-2v-5.172l1-1z" /></svg>
                </div>
                <button onClick={() => setSelectedCategory(selectedCategory === 'Drug' ? null : 'Drug')} className="text-lg font-bold text-gray-800 hover:text-green-600">Drug</button>
              </div>
              <button onClick={() => navigate('/gwas/drug')} className="text-3xl font-bold text-green-600 hover:underline">678</button>
            </div>

            <div className={`p-5 rounded-xl border hover:shadow-lg transition-all text-left ${selectedCategory === 'Behavior' ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <button onClick={() => setSelectedCategory(selectedCategory === 'Behavior' ? null : 'Behavior')} className="text-lg font-bold text-gray-800 hover:text-purple-600">Behavior</button>
              </div>
              <button onClick={() => navigate('/gwas/behavior')} className="text-3xl font-bold text-purple-600 hover:underline">474</button>
            </div>
          </div>

          {/* Subcategory Distribution Chart - show all with fixed height */}
          {selectedCategory && subcategoryData[selectedCategory] && (
            <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{selectedCategory} - Phenotypic Subcategory Distribution</h3>
              <div className="h-96 overflow-y-auto">
                <ResponsiveContainer width="100%" height={Math.max(400, subcategoryData[selectedCategory].length * 30)}>
                  <BarChart data={subcategoryData[selectedCategory]} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 10 }} />
                    <Tooltip 
                      formatter={(value: number) => [`${value}`, 'Count']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="value" fill={selectedCategory === 'Disease' ? '#ef4444' : selectedCategory === 'Measurement' ? '#3b82f6' : selectedCategory === 'Drug' ? '#22c55e' : '#a855f7'} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </section>
      <section className="py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Analysis Methods</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.filter(f => f.id !== 7).map((feature) => (
              <div key={feature.id} id={`feature-${feature.id}`} className={`group p-4 rounded-xl border transition-all cursor-pointer bg-white ${activeFeature === feature.id ? 'border-[#3b82f6] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                onMouseEnter={() => setActiveFeature(feature.id)} onMouseLeave={() => setActiveFeature(null)}
                onClick={() => {
                  if (feature.id === 1) {
                    window.open('/heatmap/final_heatmap.html', '_blank');
                  } else if (feature.id === 2) {
                    window.open('/mr/MR.html', '_blank');
                  } else if (feature.id === 3) {
                    window.open('/coloc/coloc.html', '_blank');
                  } else if (feature.id === 4) {
                    window.open('/smr/smr.html', '_blank');
                  } else if (feature.id === 5) {
                    navigate('/gene-causal');
                  } else if (feature.id === 6) {
                    window.open('/phenotypic/phenotypic.html', '_blank');
                  }
                }}>
                <div className="w-full h-32 mb-3 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-contain" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">{feature.id}</span>
                  <h3 className="text-base font-semibold text-gray-800 group-hover:text-[#3b82f6] transition-colors">{feature.title}</h3>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Feature 7: Full-width tall card */}
          <div id="feature-7" className="mt-6 group p-6 rounded-xl border transition-all cursor-pointer bg-white border-gray-200 hover:border-[#3b82f6] hover:shadow-lg"
            onClick={() => navigate('/bngwas-network')}>
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              <div className="w-full lg:w-1/3 h-48 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                <img src="/Help_pic/method7_bngwas.svg" alt="Disease Causal Network Identification" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">7</span>
                  <h3 className="text-lg font-semibold text-gray-800 group-hover:text-[#3b82f6] transition-colors">Disease Causal Network Identification</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">Identifying causal regulatory networks of phenotypes based on BN-GWAS method, integrating cis-regulatory elements (CRE), genes and phenotypes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-5 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-gray-800 text-center mb-4">Database Outputs</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {databaseOutputs.map((output, index) => (
              <div key={index} className="p-3 bg-[#f8f7f4] rounded-lg text-center"><span className="text-sm text-gray-700">{output}</span></div>
            ))}
          </div>
        </div>
      </section>
      {/* Database Overview Images */}
      <section className="py-6 px-6 bg-[#f8f7f4]">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* CREGPnet_11 - Database Foundation Overview */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">Database Foundation Overview</h3>
              <p className="text-sm text-gray-500 mt-1">Overview of the CREGPnet database construction and GWAS summary statistics landscape.</p>
            </div>
            <div className="p-4">
              <img src="/DB_pic/CREGPnet_11.png" alt="CREGPnet Database Foundation Overview" className="w-full h-auto rounded-lg" />
            </div>
            <div className="px-5 pb-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">a</span>
                  <span className="text-gray-500 ml-1">6504 GWAS phenotypes categorized by trait type and subcategory.</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">b</span>
                  <span className="text-gray-500 ml-1">Heritability distribution across all GWAS phenotypes.</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">c</span>
                  <span className="text-gray-500 ml-1">Pairwise genetic correlation between GWAS traits (LDSC).</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">d</span>
                  <span className="text-gray-500 ml-1">Phenotype-phenotype correlation network constructed via MR analysis.</span>
                </div>
              </div>
            </div>
          </div>

          {/* CREGPnet_12 - Final Result Output */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">CREGPnet Result Output</h3>
              <p className="text-sm text-gray-500 mt-1">CREGPnet demonstrates the potential to discover disease/phenotype regulatory mechanisms through causal network analysis.</p>
            </div>
            <div className="p-4">
              <img src="/DB_pic/CREGPnet_12.png" alt="CREGPnet Result Output" className="w-full h-auto rounded-lg" />
            </div>
            <div className="px-5 pb-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">a</span>
                  <span className="text-gray-500 ml-1">Causal regulatory network of HDL (High-Density Lipoprotein) phenotypes.</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">b</span>
                  <span className="text-gray-500 ml-1">Direct regulation pathway of HDL phenotypes, highlighting key regulatory genes.</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">c</span>
                  <span className="text-gray-500 ml-1">Network community analysis of the HDL regulatory network.</span>
                </div>
                <div className="bg-[#f8f7f4] rounded-lg p-3">
                  <span className="font-semibold text-gray-700">d</span>
                  <span className="text-gray-500 ml-1">KEGG pathway enrichment analysis of HDL-regulated genes, associated with lipid metabolism.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer className="py-3 px-6 border-t border-gray-200">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="text-gray-400 text-xs">© 2026 CREGPnet. All rights reserved.</p>
          <div className="flex gap-4">
            <button className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Documentation</button>
            <button className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Network Exploration Page
function NetworkPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <h1 className="text-xl font-bold text-gray-800">Causal Regulatory Network</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Using the genome-wide association study (GWAS) summary statistics from European and East Asian, CREGPnet is not only a comprehensive database designed for collecting the shared genetic basis between two complex traits, but also a server designed for estimate and visualize the shared genetic basis. CREGPnet aims to make it available for biologists and epidemiologist to investigate the common mechanism and reveal potential pathological associations and biological pathways.
            </p>
            <p className="text-gray-600 leading-relaxed">
              CREGPnet performs analysis based on seven tools in two levels: i) Trait Level: it integrates LDSC, LAVA, MR and coloc in block to perform analysis, including genome-wide and local genetic correlation estimation, causal relationship definition and pleiotropy; ii) Molecular Level: it provides SMR, coloc in expression Quantitative Trait Loci (eQTL) and INTACT to complete analysis including causal gene definition, pleiotropy definition and integration analysis.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Example: Obesity and Cardiovascular Diseases</h2>
            <p className="text-gray-600 mb-6">
              When you select a trait pair, CREGPnet provides the results in two levels with figures and tables. In this example, we treat <strong>obesity (GHB1019)</strong> and <strong>cardiovascular diseases (excluding rheumatic etc) (GHB1087)</strong> as trait1 and trait2, respectively.
            </p>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">1. Output for LDSC</h3>
              <p className="text-gray-600 text-sm mb-3">We use LDSC to define the genetic correlation between the two traits.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">p1</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">p2</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">rg</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">se</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">z</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">p</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">Obesity</td><td className="px-3 py-2 border-b">Cardiovascular_diseases</td><td className="px-3 py-2 border-b">0.568</td><td className="px-3 py-2 border-b">0.027</td><td className="px-3 py-2 border-b">21.264</td><td className="px-3 py-2 border-b">2.432e-100</td></tr></tbody></table></div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">2. Output for LAVA</h3>
              <p className="text-gray-600 text-sm mb-3">We use LAVA to define the local genetic correlation between the two traits.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">phen1</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">phen2</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">rho</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">rho.lower</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">rho.upper</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">p</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">Obesity</td><td className="px-3 py-2 border-b">Cardiovascular_diseases</td><td className="px-3 py-2 border-b">0.342</td><td className="px-3 py-2 border-b">0.174</td><td className="px-3 py-2 border-b">0.513</td><td className="px-3 py-2 border-b">8.928e-05</td></tr></tbody></table></div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">3. Output for MR</h3>
              <p className="text-gray-600 text-sm mb-3">Mendelian Randomization defines the causal relationship between exposure and outcome.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">exposure</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">outcome</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">method</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">nsnp</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">b</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">pval</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">or</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">Obesity</td><td className="px-3 py-2 border-b">Cardiovascular_diseases</td><td className="px-3 py-2 border-b">Inverse variance weighted</td><td className="px-3 py-2 border-b">140</td><td className="px-3 py-2 border-b">0.181</td><td className="px-3 py-2 border-b">1.620e-57</td><td className="px-3 py-2 border-b">1.200</td></tr></tbody></table></div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">4. Output for Coloc in LD Block</h3>
              <p className="text-gray-600 text-sm mb-3">The whole genome is split into some LD blocks. Define the pleiotropy for the two traits in each block.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">nsnps</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP.H0.abf</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP.H1.abf</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP.H2.abf</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP.H3.abf</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP.H4.abf</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">245</td><td className="px-3 py-2 border-b">0.892</td><td className="px-3 py-2 border-b">0.097</td><td className="px-3 py-2 border-b">0.020</td><td className="px-3 py-2 border-b">1.644e-04</td><td className="px-3 py-2 border-b">0.009</td></tr></tbody></table></div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">5. Output for SMR</h3>
              <p className="text-gray-600 text-sm mb-3">Summary Mendelian Randomization defines the causal gene by the selected IVs. In this case, CIB4 is found as common causal gene.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Gene</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Chr</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Beta_SMR</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">P_SMR</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">P_HEIDI</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">CIB4</td><td className="px-3 py-2 border-b">2</td><td className="px-3 py-2 border-b">0.156</td><td className="px-3 py-2 border-b">3.21e-08</td><td className="px-3 py-2 border-b">0.234</td></tr></tbody></table></div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">6. Output for Coloc in eQTL</h3>
              <p className="text-gray-600 text-sm mb-3">coloc defines the pleiotropy contributing to both gene expression and trait.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">snp</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">SNP.PP.H4</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Gene</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Model</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">rs10082353</td><td className="px-3 py-2 border-b">1.812e-08</td><td className="px-3 py-2 border-b">A1CF</td><td className="px-3 py-2 border-b">GTEx_Pancreas</td></tr></tbody></table></div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">7. Output for INTACT</h3>
              <p className="text-gray-600 text-sm mb-3">INTACT complements the result of SMR and coloc.</p>
              <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Gene</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PPH4_coloc</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Z_SMR</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">PP_INTACT</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">ABHD12</td><td className="px-3 py-2 border-b">0.613</td><td className="px-3 py-2 border-b">2.763</td><td className="px-3 py-2 border-b">0.547</td></tr></tbody></table></div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Summary of the Selected Methods</h2>
            <p className="text-gray-600 mb-4">CREGPnet integrates seven methods to decode the shared genetic basis.</p>
            <div className="overflow-x-auto"><table className="w-full text-sm border border-gray-200"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Methods</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Category</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Ancestry</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Programming Language</th><th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Software/Package</th></tr></thead><tbody><tr><td className="px-3 py-2 border-b">LDSC</td><td className="px-3 py-2 border-b">Trait Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">Python</td><td className="px-3 py-2 border-b">LDSC</td></tr><tr><td className="px-3 py-2 border-b">LAVA</td><td className="px-3 py-2 border-b">Trait Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">R</td><td className="px-3 py-2 border-b">LAVA</td></tr><tr><td className="px-3 py-2 border-b">MR</td><td className="px-3 py-2 border-b">Trait Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">R</td><td className="px-3 py-2 border-b">TwoSampleMR</td></tr><tr><td className="px-3 py-2 border-b">coloc in block</td><td className="px-3 py-2 border-b">Trait Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">R</td><td className="px-3 py-2 border-b">coloc</td></tr><tr><td className="px-3 py-2 border-b">SMR</td><td className="px-3 py-2 border-b">Molecular Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">cpp</td><td className="px-3 py-2 border-b">SMR</td></tr><tr><td className="px-3 py-2 border-b">coloc in eQTL</td><td className="px-3 py-2 border-b">Molecular Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">R</td><td className="px-3 py-2 border-b">coloc</td></tr><tr><td className="px-3 py-2 border-b">INTACT</td><td className="px-3 py-2 border-b">Molecular Level</td><td className="px-3 py-2 border-b">EUR, EAS</td><td className="px-3 py-2 border-b">R</td><td className="px-3 py-2 border-b">INTCAT</td></tr></tbody></table></div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#3b82f6]/20 to-[#8b5cf6]/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-[#3b82f6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
            <p className="text-gray-500 max-w-xl mx-auto mb-6">
              Explore the causal regulatory network of phenotypes, including genetic correlations, Mendelian randomization results, and CRE-gene-phenotype relationships.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-800 mb-1">Disease-Disease</div>
                <div className="text-sm text-gray-500">Genetic correlation network between diseases</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-800 mb-1">Disease-Measurement</div>
                <div className="text-sm text-gray-500">Causal relationships between diseases and measurements</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-800 mb-1">CRE-Gene Network</div>
                <div className="text-sm text-gray-500">Regulatory element targeting relationships</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// GWAS List Page Component
function GWASListPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'phenotype' | 'phenotypicCategory' | 'phenotypicSubcategory'>('phenotype');
  const [selectedGWAS, setSelectedGWAS] = useState<typeof finnGenData[0] | null>(null);
  const [mrResults, setMrResults] = useState<any[]>([]);
  const [mrLoading, setMrLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [traitInput, setTraitInput] = useState('');
  const [matchingTraits, setMatchingTraits] = useState<typeof finnGenData[]>([]);
  const [traitSearched, setTraitSearched] = useState(false);
  const [traitMrResults, setTraitMrResults] = useState<Record<string, any[]>>({});
  const [traitMrLoading, setTraitMrLoading] = useState<Record<string, boolean>>({});
  const pageSize = 20;

  // CSV download helper for MR results
  const downloadMRResultsCSV = (data: any[]) => {
    const columns = ['outcome', 'exposure', 'method', 'nsnp', 'b', 'se', 'pval', 'or'];
    const csvHeader = columns.map(c => `"${c}"`).join(',');
    const csvRows = data.map(row =>
      columns.map(c => {
        const val = row[c] ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = csvHeader + '\n' + csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'MR_Results.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Load MR results when GWAS is selected
  useEffect(() => {
    if (selectedGWAS) {
      setMrLoading(true);
      fetch(`/DGWAS/${selectedGWAS.accessionId}.gz`)
        .then(res => res.arrayBuffer())
        .then(buffer => {
          const decompressed = pako.inflate(new Uint8Array(buffer), { to: 'string' });
          const lines = decompressed.split('\n');
          const headers = parseCSVLine(lines[0]);
          const data = lines.slice(1).filter(line => line.trim()).map(line => {
            const values = parseCSVLine(line);
            const obj: any = {};
            headers.forEach((h, i) => {
              obj[h] = values[i];
            });
            return obj;
          });
          setMrResults(data);
          setMrLoading(false);
        })
        .catch(err => {
          console.error('Failed to load MR results:', err);
          setMrResults([]);
          setMrLoading(false);
        });
    } else {
      setMrResults([]);
    }
  }, [selectedGWAS]);

  // Handle trait search and load MR results for all matching traits
  const handleTraitSearch = () => {
    if (!traitInput.trim()) return;
    
    setTraitSearched(true);
    const searchTermLower = traitInput.toLowerCase();
    
    // Find all matching GWAS entries by phenotype name from finnGenData
    const matching = finnGenData.filter(item => 
      item.phenotype && item.phenotype.toLowerCase().includes(searchTermLower)
    );
    
    setMatchingTraits(matching);
    
    // Load MR results for each matching trait
    const initialLoadingState: Record<string, boolean> = {};
    matching.forEach(item => {
      initialLoadingState[item.accessionId] = true;
    });
    setTraitMrLoading(initialLoadingState);
    
    matching.forEach(item => {
      fetch(`/DGWAS/${item.accessionId}.gz`)
        .then(res => res.arrayBuffer())
        .then(buffer => {
          const decompressed = pako.inflate(new Uint8Array(buffer), { to: 'string' });
          const lines = decompressed.split('\n');
          const headers = parseCSVLine(lines[0]);
          const data = lines.slice(1).filter(line => line.trim()).map(line => {
            const values = parseCSVLine(line);
            const obj: any = {};
            headers.forEach((h, i) => {
              obj[h] = values[i];
            });
            return obj;
          });
          setTraitMrResults(prev => ({ ...prev, [item.accessionId]: data }));
          setTraitMrLoading(prev => ({ ...prev, [item.accessionId]: false }));
        })
        .catch(err => {
          console.error(`Failed to load MR results for ${item.accessionId}:`, err);
          setTraitMrResults(prev => ({ ...prev, [item.accessionId]: [] }));
          setTraitMrLoading(prev => ({ ...prev, [item.accessionId]: false }));
        });
    });
  };

  const typeLabels: Record<string, { en: string; color: string }> = {
    disease: { en: 'Disease', color: 'red' },
    measurement: { en: 'Measurement', color: 'blue' },
    drug: { en: 'Drug', color: 'green' },
    behavior: { en: 'Behavior', color: 'purple' }
  };

  const filteredData = finnGenData.filter(item => item.phenotypicCategory.toLowerCase() === type);
  const label = typeLabels[type || 'disease'] || typeLabels.disease;

  const searchFiltered = searchTerm 
    ? filteredData.filter(item => {
        const searchValue = searchTerm.toLowerCase();
        if (searchField === 'phenotype') {
          return item.phenotype.toLowerCase().includes(searchValue);
        } else if (searchField === 'phenotypicCategory') {
          return item.phenotypicCategory.toLowerCase().includes(searchValue);
        } else {
          return item.phenotypicSubcategory.toLowerCase().includes(searchValue);
        }
      })
    : filteredData;

  const totalPages = Math.ceil(searchFiltered.length / pageSize);
  const paginatedData = searchFiltered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="bg-white border-b border-gray-200 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">{label.en} - GWAS List ({filteredData.length})</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="py-6 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Summary for one trait */}
          <div className="mb-6 bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Summary for One Trait</h2>
            <p className="text-gray-600 text-sm mb-4">
              Enter a trait name to search across GWAS phenotype names and display MR analysis results for all matching entries.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Enter trait name (e.g., obesity, height, diabetes...)"
                value={traitInput}
                onChange={(e) => setTraitInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTraitSearch()}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50"
              />
              <button
                onClick={handleTraitSearch}
                className="px-6 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors font-medium"
              >
                Submit
              </button>
            </div>

            {traitSearched && (
              <div>
                {matchingTraits.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg">No matching GWAS found for "{traitInput}"</p>
                    <p className="text-sm mt-2">Please try a different trait name</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Found <span className="font-semibold text-[#3b82f6]">{matchingTraits.length}</span> matching GWAS entries for "{traitInput}"
                    </p>
                    <div className="space-y-6">
                      {matchingTraits.map((item) => (
                        <div key={item.accessionId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-800">{item.phenotype}</h4>
                              <p className="text-xs text-gray-500">
                                Category: {item.phenotypicCategory} | Subcategory: {item.phenotypicSubcategory}
                              </p>
                            </div>
                            <a 
                              href={item.download} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#3b82f6] hover:underline text-xs"
                            >
                              Download
                            </a>
                          </div>
                          
                          <div className="mb-3">
                            <img 
                              src={`/mr_spiral_plots/${item.accessionId}.MR.png`} 
                              alt={`MR Results for ${item.accessionId}`}
                              className="max-w-full h-auto rounded-lg"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>

                          {traitMrLoading[item.accessionId] ? (
                            <div className="text-center py-4 text-gray-500 text-sm">Loading MR results...</div>
                          ) : traitMrResults[item.accessionId] && traitMrResults[item.accessionId].length > 0 ? (
                            <div className="overflow-x-auto">
                              <div className="flex justify-between items-center mb-2">
                                <p className="text-xs text-gray-500">MR Analysis Results ({traitMrResults[item.accessionId].length} rows)</p>
                                <button 
                                  onClick={() => downloadMRResultsCSV(traitMrResults[item.accessionId])}
                                  className="px-3 py-1 bg-emerald-500 text-white text-xs rounded hover:bg-emerald-600 transition-colors"
                                >
                                  Download CSV
                                </button>
                              </div>
                              <table className="w-full text-xs border border-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">outcome</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">exposure</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">method</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">nsnp</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">b</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">se</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">pval</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">or</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {traitMrResults[item.accessionId].slice(0, 10).map((row: any, idx: number) => (
                                    <tr key={idx} className="hover:bg-white">
                                      <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.outcome}>{row.outcome}</td>
                                      <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.exposure}>{row.exposure}</td>
                                      <td className="px-3 py-2 text-gray-600">{row.method}</td>
                                      <td className="px-3 py-2 text-gray-600">{row.nsnp}</td>
                                      <td className="px-3 py-2 text-gray-600">{parseFloat(row.b).toFixed(4)}</td>
                                      <td className="px-3 py-2 text-gray-600">{parseFloat(row.se).toFixed(4)}</td>
                                      <td className="px-3 py-2 text-gray-600">{parseFloat(row.pval).toFixed(6)}</td>
                                      <td className="px-3 py-2 text-gray-600">{parseFloat(row.or).toFixed(4)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {traitMrResults[item.accessionId].length > 10 && (
                                <p className="text-xs text-gray-500 mt-2">Showing 10 of {traitMrResults[item.accessionId].length} results</p>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500 text-sm">No MR results available</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary for two traits - GWAS List */}
          <div className="mb-4 flex gap-2">
            <select 
              value={searchField}
              onChange={(e) => setSearchField(e.target.value as any)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50 text-sm"
            >
              <option value="phenotype">phenotype</option>
              <option value="phenotypicCategory">Phenotypic category</option>
              <option value="phenotypicSubcategory">Phenotypic subcategory</option>
            </select>
            <input 
              type="text" 
              placeholder={`Search by ${searchField}...`}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50"
            />
          </div>

          {selectedGWAS && (
            <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">MR Results: {selectedGWAS.phenotype}</h3>
                <button 
                  onClick={() => setSelectedGWAS(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-4">
                <img 
                  src={`/mr_spiral_plots/${selectedGWAS.accessionId}.MR.png`} 
                  alt={`MR Results for ${selectedGWAS.accessionId}`}
                  className="max-w-full h-auto rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              {mrLoading ? (
                <div className="text-center py-4 text-gray-500">Loading MR results...</div>
              ) : mrResults.length > 0 ? (
                <div className="overflow-x-auto mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs text-gray-500">MR Analysis Results ({mrResults.length} rows)</p>
                    <button 
                      onClick={() => downloadMRResultsCSV(mrResults)}
                      className="px-3 py-1 bg-emerald-500 text-white text-xs rounded hover:bg-emerald-600 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">outcome</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">exposure</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">method</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">nsnp</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">b</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">se</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">pval</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">or</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mrResults.slice(0, 20).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.outcome}>{row.outcome}</td>
                          <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.exposure}>{row.exposure}</td>
                          <td className="px-3 py-2 text-gray-600">{row.method}</td>
                          <td className="px-3 py-2 text-gray-600">{row.nsnp}</td>
                          <td className="px-3 py-2 text-gray-600">{parseFloat(row.b).toFixed(4)}</td>
                          <td className="px-3 py-2 text-gray-600">{parseFloat(row.se).toFixed(4)}</td>
                          <td className="px-3 py-2 text-gray-600">{parseFloat(row.pval).toFixed(6)}</td>
                          <td className="px-3 py-2 text-gray-600">{parseFloat(row.or).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {mrResults.length > 20 && (
                    <p className="text-xs text-gray-500 mt-2">Showing 20 of {mrResults.length} results</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">No MR results available</div>
              )}
              <div className="text-sm text-gray-500 mt-4">
                <p>MR Analysis Results for: {selectedGWAS.phenotype}</p>
                <p className="mt-1">Category: {selectedGWAS.phenotypicCategory} | Subcategory: {selectedGWAS.phenotypicSubcategory}</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">sources</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">phenocode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">phenotype</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phenotypic category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phenotypic subcategory</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">pmid</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.sources}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs max-w-xs truncate" title={item.phenocode}>{item.phenocode}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={item.phenotype}>{item.phenotype}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.phenotypicCategory}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.phenotypicSubcategory}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.pmid || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <button 
                          onClick={() => setSelectedGWAS(item)}
                          className="text-[#3b82f6] hover:underline text-xs mr-2"
                        >
                          View MR
                        </button>
                        <a href={item.download} target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] hover:underline text-xs">Download</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, searchFiltered.length)} of {searchFiltered.length}
                </div>
                <div className="flex gap-1 items-center">
                  <button 
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-xs text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Gene Causal Effect Page Component
function GeneCausalPage() {
  const navigate = useNavigate();
  const [geneInput, setGeneInput] = useState('');
  const [geneSearched, setGeneSearched] = useState(false);
  const [selectedGene, setSelectedGene] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [geneList, setGeneList] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch gene list from manifest on mount
  useEffect(() => {
    fetch('/MR_spiral_plots_per_gene/manifest.json')
      .then(res => res.json())
      .then(data => {
        const genes = Array.isArray(data) ? data : (data.genes || []);
        setGeneList(genes.sort());
      })
      .catch(() => {});
  }, []);

  // Parse CSV line handling quoted fields with commas
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (inQuotes) {
        if (char === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
    }
    result.push(current);
    return result;
  };

  // Load gene data
  const loadGene = async (gene: string) => {
    setSelectedGene(gene);
    setLoading(true);
    setError(null);
    setCsvData([]);
    setCsvHeaders([]);
    try {
      const res = await fetch(`/split_files/${gene}.csv.gz`);
      if (!res.ok) throw new Error(`File not found: /split_files/${gene}.csv.gz (status ${res.status})`);
      const buffer = await res.arrayBuffer();
      console.log('Fetched gz buffer, size:', buffer.byteLength);
      const uint8 = new Uint8Array(buffer);
      const decompressed = pako.inflate(uint8, { to: 'string' });
      console.log('Decompressed length:', decompressed.length);
      console.log('First 200 chars:', decompressed.substring(0, 200));
      const lines = decompressed.trim().split('\n');
      if (lines.length > 0) {
        const headers = parseCSVLine(lines[0]);
        console.log('Headers:', headers);
        const rows = lines.slice(1).map(line => {
          const values = parseCSVLine(line);
          const row: Record<string, string> = {};
          headers.forEach((h, i) => { row[h] = values[i] || ''; });
          return row;
        });
        const excludeCols = ['id.exposure', 'id.outcome'];
        const filteredHeaders = headers.filter(h => !excludeCols.includes(h));
        console.log('Rows count:', rows.length);
        setCsvHeaders(filteredHeaders);
        setCsvData(rows);
      }
    } catch (err: any) {
      console.error('Error loading gene data:', err);
      setError(err.message || 'Failed to load gene data');
      setCsvHeaders([]);
      setCsvData([]);
    }
    setLoading(false);
  };

  // Handle search submit
  const handleGeneSearch = () => {
    const term = geneInput.trim().toUpperCase();
    if (!term) return;
    setGeneSearched(true);
    if (geneList.includes(term)) {
      loadGene(term);
    } else {
      setSelectedGene(null);
      setCsvData([]);
      setCsvHeaders([]);
    }
  };

  // Download CSV
  const downloadCSV = () => {
    if (!selectedGene || csvHeaders.length === 0) return;
    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => csvHeaders.map(h => row[h] || '').join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedGene}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const annotationText = (gene: string) =>
    `Spiral plot of Mendelian randomization (MR) results for gene ${gene} against multiple disease outcomes. ` +
    `Each point represents a distinct outcome (phenotype) tested for its causal association with the exposure gene ${gene} using the inverse‑variance weighted (IVW) method. ` +
    `The plot is arranged in a polar coordinate system with a 300° angular span (starting from the top, clockwise), where the radial distance from the center corresponds to the statistical significance expressed as ‑log₁₀(p‑value) of the MR estimate. ` +
    `Points farther from the center indicate stronger evidence against the null hypothesis. The size of each point is proportional to the absolute magnitude of the estimated causal effect (|β|), with larger points representing stronger effect sizes. ` +
    `The color indicates the direction of the effect: red for positive (risk‑increasing) and blue for negative (protective) associations. ` +
    `The plot is based on genome‑wide eQTL summary statistics (used as instrumental variables, with a significance threshold of 5×10⁻⁸) and GWAS summary statistics for each outcome, restricted to independent instrumental SNPs after clumping. ` +
    `Only outcomes with complete MR estimates are displayed. This visualization allows rapid assessment of the breadth and strength of pleiotropic associations for a given gene across a large number of traits.`;

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-xl font-bold text-gray-800">Overall Causal Effect for Gene</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary for One Gene */}
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Summary for One Gene</h2>
          <p className="text-gray-600 text-sm mb-4">
            Enter a gene symbol to query the overall causal effect of predicted gene expression on the trait using IVW estimate aggregating multiple cis‑eQTL instruments.
          </p>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Enter gene symbol (e.g., FIRRM, ABO, BRCA1...)"
              value={geneInput}
              onChange={(e) => setGeneInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGeneSearch()}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50"
            />
            <button
              onClick={handleGeneSearch}
              className="px-6 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors font-medium"
            >
              Submit
            </button>
          </div>

          {/* Available genes */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-600">Available Genes ({geneList.length})</p>
            </div>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
              <div className="flex flex-wrap gap-1.5">
                {geneList.map(gene => (
                  <button
                    key={gene}
                    onClick={() => { setGeneInput(gene); setGeneSearched(true); setSelectedGene(gene); loadGene(gene); }}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                      selectedGene === gene
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-500'
                    }`}
                  >
                    {gene}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search results */}
          {geneSearched && (
            <div>
              {selectedGene ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-800">Gene: {selectedGene}</h4>
                      <p className="text-xs text-gray-500">Overall Causal Effect Analysis</p>
                    </div>
                  </div>

                  {/* Spiral Plot */}
                  <div className="mb-3">
                    <img
                      src={`/MR_spiral_plots_per_gene/${selectedGene}.png`}
                      alt={`MR Spiral Plot for ${selectedGene}`}
                      className="max-w-full h-auto rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">{annotationText(selectedGene)}</p>
                  </div>

                  {/* MR Results Table */}
                  {loading ? (
                    <div className="text-center py-4 text-gray-500 text-sm">Loading MR results...</div>
                  ) : error ? (
                    <div className="text-center py-4 text-red-500 text-sm">
                      <p>Error: {error}</p>
                      <p className="text-xs text-gray-400 mt-1">Check browser console for details</p>
                    </div>
                  ) : csvData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-xs text-gray-500">MR Analysis Results ({csvData.length} rows)</p>
                        <button
                          onClick={downloadCSV}
                          className="px-3 py-1 bg-emerald-500 text-white text-xs rounded hover:bg-emerald-600 transition-colors"
                        >
                          Download CSV
                        </button>
                      </div>
                      <table className="w-full text-xs border border-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            {csvHeaders.map((h, i) => (
                              <th key={i} className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {csvData.slice(0, 10).map((row, idx) => (
                            <tr key={idx} className="hover:bg-white">
                              {csvHeaders.map((h, ci) => (
                                <td key={ci} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row[h]}>{row[h]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvData.length > 10 && (
                        <p className="text-xs text-gray-500 mt-2">Showing 10 of {csvData.length} results</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">No MR results available for {selectedGene}</div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg">No matching gene found for "{geneInput}"</p>
                  <p className="text-sm mt-2">Please try a different gene symbol</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// BN-GWAS Disease Causal Network Page Component
function BNGWASNetworkPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'method' | 'example' | 'download'>('overview');
  const [searchTerm, setSearchTerm] = useState('');

  // Example network data
  const exampleCREs = [
    { id: 'CRE_001', chr: '1', start: '11845787', end: '11847132', type: 'Enhancer', targetGenes: ['PERM1'], phenotypes: ['BMI', 'Obesity'] },
    { id: 'CRE_002', chr: '2', start: '65268947', end: '65271203', type: 'Promoter', targetGenes: ['APOB', 'IFI44L'], phenotypes: ['LDL', 'Cholesterol'] },
    { id: 'CRE_003', chr: '6', start: '31268749', end: '31270156', type: 'Enhancer', targetGenes: ['HLA-DRB1'], phenotypes: ['Rheumatoid Arthritis', 'Type 1 Diabetes'] },
    { id: 'CRE_004', chr: '9', start: '22125503', end: '22127741', type: 'CTCF Binding', targetGenes: ['CDKN2A', 'CDKN2B'], phenotypes: ['Coronary Artery Disease', 'Stroke'] },
    { id: 'CRE_005', chr: '12', start: '112241766', end: '112243502', type: 'Enhancer', targetGenes: ['TBX3', 'TBX5'], phenotypes: ['QT Interval', 'Heart Rate'] },
    { id: 'CRE_006', chr: '19', start: '45411941', end: '45413826', type: 'Promoter', targetGenes: ['APOE'], phenotypes: ['Alzheimers Disease', 'Lipid Levels'] },
  ];

  const exampleGenes = [
    { gene: 'APOE', creCount: 3, phenotypeCount: 5, causalScore: 0.92 },
    { gene: 'CDKN2A', creCount: 2, phenotypeCount: 4, causalScore: 0.88 },
    { gene: 'HLA-DRB1', creCount: 2, phenotypeCount: 6, causalScore: 0.85 },
    { gene: 'APOB', creCount: 2, phenotypeCount: 3, causalScore: 0.79 },
    { gene: 'PERM1', creCount: 1, phenotypeCount: 2, causalScore: 0.71 },
    { gene: 'TBX3', creCount: 1, phenotypeCount: 2, causalScore: 0.68 },
  ];

  const exampleEdges = [
    { from: 'CRE_001', to: 'PERM1', type: 'regulatory', confidence: 0.87 },
    { from: 'PERM1', to: 'BMI', type: 'causal', confidence: 0.82 },
    { from: 'PERM1', to: 'Obesity', type: 'causal', confidence: 0.79 },
    { from: 'CRE_002', to: 'APOB', type: 'regulatory', confidence: 0.93 },
    { from: 'CRE_002', to: 'IFI44L', type: 'regulatory', confidence: 0.76 },
    { from: 'APOB', to: 'LDL', type: 'causal', confidence: 0.95 },
    { from: 'APOB', to: 'Cholesterol', type: 'causal', confidence: 0.88 },
    { from: 'CRE_003', to: 'HLA-DRB1', type: 'regulatory', confidence: 0.91 },
    { from: 'HLA-DRB1', to: 'Rheumatoid Arthritis', type: 'causal', confidence: 0.90 },
    { from: 'HLA-DRB1', to: 'Type 1 Diabetes', type: 'causal', confidence: 0.86 },
    { from: 'CRE_004', to: 'CDKN2A', type: 'regulatory', confidence: 0.89 },
    { from: 'CRE_004', to: 'CDKN2B', type: 'regulatory', confidence: 0.85 },
    { from: 'CDKN2A', to: 'Coronary Artery Disease', type: 'causal', confidence: 0.84 },
    { from: 'CDKN2A', to: 'Stroke', type: 'causal', confidence: 0.78 },
    { from: 'CRE_005', to: 'TBX3', type: 'regulatory', confidence: 0.82 },
    { from: 'TBX3', to: 'QT Interval', type: 'causal', confidence: 0.77 },
    { from: 'CRE_006', to: 'APOE', type: 'regulatory', confidence: 0.96 },
    { from: 'APOE', to: 'Alzheimers Disease', type: 'causal', confidence: 0.94 },
    { from: 'APOE', to: 'Lipid Levels', type: 'causal', confidence: 0.91 },
  ];

  const filteredCREs = exampleCREs.filter(cre =>
    cre.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cre.targetGenes.some(g => g.toLowerCase().includes(searchTerm.toLowerCase())) ||
    cre.phenotypes.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredGenes = exampleGenes.filter(g =>
    g.gene.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'overview' as const, label: 'Overview' },
    { id: 'method' as const, label: 'Method' },
    { id: 'example' as const, label: 'Example Network' },
  ];

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-xl font-bold text-gray-800">Disease Causal Network Identification</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-gray-200 p-1 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#3b82f6] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The <strong>Disease Causal Network Identification</strong> module leverages the <strong>BN-GWAS (Bayesian Network-based Genome-Wide Association Study)</strong> method to systematically identify causal regulatory networks underlying complex diseases and traits.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                This approach integrates three key biological layers:
              </p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">CREs</h3>
                  </div>
                  <p className="text-sm text-gray-600">Cis-Regulatory Elements including enhancers, promoters, and insulators that regulate gene expression</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">Genes</h3>
                  </div>
                  <p className="text-sm text-gray-600">Target genes whose expression is regulated by CREs and causally associated with phenotypes</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">Phenotypes</h3>
                  </div>
                  <p className="text-sm text-gray-600">Disease and trait phenotypes with GWAS summary statistics for causal inference</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                By constructing directed acyclic graphs (DAGs) from GWAS summary statistics, BN-GWAS infers the most probable causal structure connecting CREs, genes, and phenotypes, revealing the regulatory mechanisms through which genetic variants influence disease risk.
              </p>
            </div>

            {/* Network Visualization Placeholder */}
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Network Architecture</h2>
              <div className="relative bg-gradient-to-b from-gray-50 to-white rounded-xl border border-gray-100 p-8 overflow-hidden">
                {/* SVG Network Diagram */}
                <svg viewBox="0 0 800 400" className="w-full h-auto">
                  {/* CRE Layer */}
                  <rect x="50" y="30" width="120" height="40" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="1.5" />
                  <text x="110" y="55" textAnchor="middle" className="text-xs font-medium" fill="#c2410c">CRE_001</text>
                  <rect x="200" y="30" width="120" height="40" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="1.5" />
                  <text x="260" y="55" textAnchor="middle" className="text-xs font-medium" fill="#c2410c">CRE_002</text>
                  <rect x="350" y="30" width="120" height="40" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="1.5" />
                  <text x="410" y="55" textAnchor="middle" className="text-xs font-medium" fill="#c2410c">CRE_004</text>
                  <rect x="500" y="30" width="120" height="40" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="1.5" />
                  <text x="560" y="55" textAnchor="middle" className="text-xs font-medium" fill="#c2410c">CRE_006</text>
                  
                  {/* Layer Label */}
                  <text x="700" y="55" className="text-xs font-semibold" fill="#9ca3af">CREs</text>

                  {/* Gene Layer */}
                  <rect x="100" y="160" width="100" height="40" rx="8" fill="#bbf7d0" stroke="#22c55e" strokeWidth="1.5" />
                  <text x="150" y="185" textAnchor="middle" className="text-xs font-medium" fill="#166534">PERM1</text>
                  <rect x="250" y="160" width="100" height="40" rx="8" fill="#bbf7d0" stroke="#22c55e" strokeWidth="1.5" />
                  <text x="300" y="185" textAnchor="middle" className="text-xs font-medium" fill="#166534">APOB</text>
                  <rect x="400" y="160" width="100" height="40" rx="8" fill="#bbf7d0" stroke="#22c55e" strokeWidth="1.5" />
                  <text x="450" y="185" textAnchor="middle" className="text-xs font-medium" fill="#166534">CDKN2A</text>
                  <rect x="550" y="160" width="100" height="40" rx="8" fill="#bbf7d0" stroke="#22c55e" strokeWidth="1.5" />
                  <text x="600" y="185" textAnchor="middle" className="text-xs font-medium" fill="#166534">APOE</text>

                  {/* Layer Label */}
                  <text x="700" y="185" className="text-xs font-semibold" fill="#9ca3af">Genes</text>

                  {/* Phenotype Layer */}
                  <rect x="30" y="310" width="110" height="40" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="85" y="335" textAnchor="middle" className="text-xs font-medium" fill="#1e40af">BMI</text>
                  <rect x="160" y="310" width="110" height="40" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="215" y="335" textAnchor="middle" className="text-xs font-medium" fill="#1e40af">LDL</text>
                  <rect x="300" y="310" width="120" height="40" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="360" y="335" textAnchor="middle" className="text-xs font-medium" fill="#1e40af">CAD</text>
                  <rect x="450" y="310" width="120" height="40" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="510" y="335" textAnchor="middle" className="text-xs font-medium" fill="#1e40af">Alzheimers</text>
                  <rect x="590" y="310" width="110" height="40" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="645" y="335" textAnchor="middle" className="text-xs font-medium" fill="#1e40af">Stroke</text>

                  {/* Layer Label */}
                  <text x="720" y="335" className="text-xs font-semibold" fill="#9ca3af">Phenotypes</text>

                  {/* CRE -> Gene arrows */}
                  <line x1="110" y1="70" x2="150" y2="160" stroke="#f97316" strokeWidth="1.5" markerEnd="url(#arrowOrange)" />
                  <line x1="260" y1="70" x2="300" y2="160" stroke="#f97316" strokeWidth="1.5" markerEnd="url(#arrowOrange)" />
                  <line x1="410" y1="70" x2="450" y2="160" stroke="#f97316" strokeWidth="1.5" markerEnd="url(#arrowOrange)" />
                  <line x1="560" y1="70" x2="600" y2="160" stroke="#f97316" strokeWidth="1.5" markerEnd="url(#arrowOrange)" />

                  {/* Gene -> Phenotype arrows */}
                  <line x1="150" y1="200" x2="85" y2="310" stroke="#22c55e" strokeWidth="1.5" markerEnd="url(#arrowGreen)" />
                  <line x1="300" y1="200" x2="215" y2="310" stroke="#22c55e" strokeWidth="1.5" markerEnd="url(#arrowGreen)" />
                  <line x1="450" y1="200" x2="360" y2="310" stroke="#22c55e" strokeWidth="1.5" markerEnd="url(#arrowGreen)" />
                  <line x1="600" y1="200" x2="510" y2="310" stroke="#22c55e" strokeWidth="1.5" markerEnd="url(#arrowGreen)" />
                  <line x1="600" y1="200" x2="645" y2="310" stroke="#22c55e" strokeWidth="1.5" markerEnd="url(#arrowGreen)" />

                  {/* Arrow markers */}
                  <defs>
                    <marker id="arrowOrange" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <path d="M0,0 L8,3 L0,6" fill="#f97316" />
                    </marker>
                    <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <path d="M0,0 L8,3 L0,6" fill="#22c55e" />
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
                <div className="text-3xl font-bold text-orange-500 mb-1">12,847</div>
                <div className="text-sm text-gray-500">CREs Annotated</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
                <div className="text-3xl font-bold text-green-500 mb-1">3,216</div>
                <div className="text-sm text-gray-500">Causal Genes</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
                <div className="text-3xl font-bold text-blue-500 mb-1">4,675</div>
                <div className="text-sm text-gray-500">Disease Phenotypes</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-center">
                <div className="text-3xl font-bold text-purple-500 mb-1">48,392</div>
                <div className="text-sm text-gray-500">Causal Edges</div>
              </div>
            </div>
          </div>
        )}

        {/* Method Tab */}
        {activeTab === 'method' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">BN-GWAS Method</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                The BN-GWAS method applies Bayesian Network modeling to GWAS summary statistics to infer causal directed acyclic graphs (DAGs) that represent the regulatory hierarchy from genetic variants through gene expression to disease phenotypes.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Data Integration</h3>
                    <p className="text-sm text-gray-600">GWAS summary statistics for phenotypes, eQTL summary statistics for gene expression, and chromatin interaction data (Hi-C, ChIP-seq) for CRE-gene links are integrated into a unified framework.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">CRE-Gene Association</h3>
                    <p className="text-sm text-gray-600">Cis-regulatory elements are linked to their target genes using chromatin conformation data and expression quantitative trait locus (eQTL) colocalization analysis, establishing the CRE → Gene regulatory layer.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Causal Inference via SMR & MR</h3>
                    <p className="text-sm text-gray-600">Summary Mendelian Randomization (SMR) and two-sample Mendelian Randomization (MR) identify causal genes by testing whether genetically predicted expression is associated with the phenotype, controlling for population stratification.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">4</div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Bayesian Network Construction</h3>
                    <p className="text-sm text-gray-600">A Bayesian Network is constructed to model the joint probability distribution over CREs, genes, and phenotypes. The structure learning algorithm (e.g., hill-climbing with BIC score) identifies the most probable DAG consistent with the data.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">5</div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Network Validation</h3>
                    <p className="text-sm text-gray-600">The inferred network is validated through cross-validation, stability selection, and comparison with known regulatory pathways from ENCODE and ROADMAP epigenomics data. Edge confidence scores are computed via bootstrap resampling.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Method Summary Table */}
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Method Summary</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Component</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Input</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Method</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Output</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-3 border-b font-medium text-gray-800">CRE-Gene Link</td>
                      <td className="px-4 py-3 border-b text-gray-600">Hi-C, ChIP-seq, ATAC-seq</td>
                      <td className="px-4 py-3 border-b text-gray-600">Chromatin Interaction + Coloc</td>
                      <td className="px-4 py-3 border-b text-gray-600">CRE → Gene edges</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 border-b font-medium text-gray-800">Gene-Phenotype Link</td>
                      <td className="px-4 py-3 border-b text-gray-600">eQTL + GWAS summary</td>
                      <td className="px-4 py-3 border-b text-gray-600">SMR / MR</td>
                      <td className="px-4 py-3 border-b text-gray-600">Gene → Phenotype edges</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 border-b font-medium text-gray-800">Network Structure</td>
                      <td className="px-4 py-3 border-b text-gray-600">All edges + p-values</td>
                      <td className="px-4 py-3 border-b text-gray-600">Bayesian Network (BIC)</td>
                      <td className="px-4 py-3 border-b text-gray-600">Optimal DAG</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-800">Confidence</td>
                      <td className="px-4 py-3 text-gray-600">Bootstrap resampling</td>
                      <td className="px-4 py-3 text-gray-600">Stability Selection</td>
                      <td className="px-4 py-3 text-gray-600">Edge confidence scores</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Example Network Tab */}
        {activeTab === 'example' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by CRE, gene, or phenotype (e.g., APOE, BMI, Enhancer...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50 text-sm"
                />
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* CRE Table */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">CRE-Gene-Phenotype Associations</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">CRE ID</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Location</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Target Genes</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Associated Phenotypes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCREs.map(cre => (
                      <tr key={cre.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b font-medium text-[#3b82f6]">{cre.id}</td>
                        <td className="px-4 py-3 border-b text-gray-600 text-xs font-mono">chr{cre.chr}:{cre.start}-{cre.end}</td>
                        <td className="px-4 py-3 border-b">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            cre.type === 'Enhancer' ? 'bg-orange-100 text-orange-700' :
                            cre.type === 'Promoter' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {cre.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-b">
                          <div className="flex flex-wrap gap-1">
                            {cre.targetGenes.map(g => (
                              <span key={g} className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">{g}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b">
                          <div className="flex flex-wrap gap-1">
                            {cre.phenotypes.map(p => (
                              <span key={p} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">{p}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredCREs.length === 0 && (
                  <p className="text-center py-8 text-gray-500 text-sm">No results found for "{searchTerm}"</p>
                )}
              </div>
            </div>

            {/* Causal Genes */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Top Causal Genes</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Gene</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Linked CREs</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Associated Phenotypes</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Causal Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGenes.map(gene => (
                      <tr key={gene.gene} className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b font-medium text-green-600">{gene.gene}</td>
                        <td className="px-4 py-3 border-b text-gray-600">{gene.creCount}</td>
                        <td className="px-4 py-3 border-b text-gray-600">{gene.phenotypeCount}</td>
                        <td className="px-4 py-3 border-b">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-[#3b82f6] rounded-full" style={{ width: `${gene.causalScore * 100}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 w-10">{(gene.causalScore * 100).toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edge List */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Network Edges (Top {exampleEdges.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Source</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Target</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exampleEdges.map((edge, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b font-medium text-gray-800">{edge.from}</td>
                        <td className="px-4 py-3 border-b font-medium text-gray-800">{edge.to}</td>
                        <td className="px-4 py-3 border-b">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            edge.type === 'regulatory' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {edge.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-b">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-[#8b5cf6] rounded-full" style={{ width: `${edge.confidence * 100}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 w-10">{(edge.confidence * 100).toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


      </main>
    </div>
  );
}

// Help Page Component
function HelpPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'docs' | 'example'>('docs');

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-xl font-bold text-gray-800">Help</h1>
        </div>
      </header>
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 flex gap-1 pt-3">
          <button
            onClick={() => setActiveTab('docs')}
            className={`px-5 py-2.5 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === 'docs'
                ? 'bg-[#f8f7f4] text-[#3b82f6] border border-gray-200 border-b-transparent -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Documentation
          </button>
          <button
            onClick={() => setActiveTab('example')}
            className={`px-5 py-2.5 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === 'example'
                ? 'bg-[#f8f7f4] text-[#3b82f6] border border-gray-200 border-b-transparent -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Example
          </button>
        </div>
      </div>
      {/* Documentation Tab */}
      {activeTab === 'docs' && (
        <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">About CREGPnet</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              <strong>CREGPnet</strong> (Comprehensive Causal Regulatory Network Database) is a platform designed for decoding the shared genetic basis between complex traits using GWAS summary statistics. It integrates cis-regulatory elements (CRE), genes, and phenotypes to build complete causal regulatory networks.
            </p>
            <p className="text-gray-600 leading-relaxed">Using the genome-wide association study (GWAS) summary statistics from European populations, CREGPnet provides both a comprehensive database and an analytical server for investigating common mechanisms and revealing potential pathological associations and biological pathways.</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Analysis Methods</h2>
            <p className="text-gray-600 mb-6">CREGPnet performs analysis based on seven tools across two levels:</p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">Trait</span>
                  Trait Level
                </h3>
                <div className="grid md:grid-cols-2 gap-3 ml-8">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">1. Genetic Correlation (LDSC)</span>
                    <p className="text-xs text-gray-500 mt-1">Defines the genome-wide genetic correlation between two traits using summary statistics.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">2. Mendelian Randomization (MR)</span>
                    <p className="text-xs text-gray-500 mt-1">Defines the causal relationship between exposure and outcome using instrumental variables.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">3. Colocalization in LD Block</span>
                    <p className="text-xs text-gray-500 mt-1">Defines pleiotropy for two traits within each LD block across the genome.</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-[#8b5cf6] text-white text-xs font-bold flex items-center justify-center">Mol</span>
                  Molecular Level
                </h3>
                <div className="grid md:grid-cols-2 gap-3 ml-8">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">4. Summary Mendelian Randomization (SMR)</span>
                    <p className="text-xs text-gray-500 mt-1">Defines causal genes by testing whether genetically predicted expression is associated with the trait.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">5. Overall Causal Effect for Gene</span>
                    <p className="text-xs text-gray-500 mt-1">IVW estimate aggregating multiple cis-eQTL instruments to test overall causal effect of gene expression.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">6. Phenotypic Co-occurrence (HyPrColoc)</span>
                    <p className="text-xs text-gray-500 mt-1">Integrates SMR and coloc results to identify genes with convergent evidence.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-800">7. Disease Causal Network (BN-GWAS)</span>
                    <p className="text-xs text-gray-500 mt-1">Identifies causal regulatory networks integrating CRE, genes, and phenotypes via Bayesian Networks.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <h3 className="font-semibold text-gray-800">Navigate to a Method</h3>
                  <p className="text-sm text-gray-600">Use the top navigation bar to jump to any analysis method, or scroll down to the Analysis Methods section on the homepage.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <h3 className="font-semibold text-gray-800">Explore Trait Pairs</h3>
                  <p className="text-sm text-gray-600">For methods like MR and SMR, select or search for trait pairs to view detailed analysis results including tables and visualizations.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <h3 className="font-semibold text-gray-800">View Interactive Visualizations</h3>
                  <p className="text-sm text-gray-600">Many methods provide interactive HTML visualizations (heatmaps, volcano plots, Manhattan plots) that open in new tabs for detailed exploration.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#3b82f6] text-white flex items-center justify-center font-bold text-sm">4</div>
                <div>
                  <h3 className="font-semibold text-gray-800">Download Data</h3>
                  <p className="text-sm text-gray-600">Results tables can be downloaded as CSV files for further analysis in your preferred bioinformatics tools.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-1">What populations are supported?</h3>
                <p className="text-sm text-gray-600">CREGPnet currently supports European (EUR) populations.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-1">How many GWAS studies are included?</h3>
                <p className="text-sm text-gray-600">The database includes over 6,500 GWAS studies across 4,675 disease, 677 measurement, 678 drug, and 474 behavior phenotypes.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-1">Can I download the results?</h3>
                <p className="text-sm text-gray-600">Yes, most analysis results can be downloaded as CSV files directly from the results tables.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Citation</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              If you use CREGPnet in your research, please cite our work. The platform provides comprehensive analysis tools for understanding the shared genetic basis between complex traits.
            </p>
          </div>
        </main>
      )}
      {/* Example Tab */}
      {activeTab === 'example' && (
        <main className="max-w-5xl mx-auto px-6 py-8 space-y-5">
          <p className="text-gray-500 text-sm">Below are step-by-step examples for each of the 7 analysis methods. Follow the instructions to try each feature.</p>

          {/* Sample Description */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Sample Description</h2>
            <p className="text-gray-600 text-sm mb-4">The example below uses trait pair: <strong>Obesity</strong> and <strong>Cardiovascular Diseases</strong> as a demonstration.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Item</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Trait 1 (Exposure)</td><td className="px-4 py-2 border-b text-gray-700">Obesity</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Trait 2 (Outcome)</td><td className="px-4 py-2 border-b text-gray-700">Cardiovascular Diseases</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Ancestry</td><td className="px-4 py-2 border-b text-gray-700">European (EUR)</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Number of IVs</td><td className="px-4 py-2 border-b text-gray-700">140</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">MR Method</td><td className="px-4 py-2 border-b text-gray-700">Inverse Variance Weighted (IVW)</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Causal Estimate (b)</td><td className="px-4 py-2 border-b text-gray-700">0.181</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">Odds Ratio (OR)</td><td className="px-4 py-2 border-b text-gray-700">1.200</td></tr>
                  <tr><td className="px-4 py-2 border-b text-gray-700">P-value</td><td className="px-4 py-2 border-b text-gray-700">1.62e-57</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Method 1: Genetic Correlation */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">1</span>
              <h3 className="text-lg font-bold text-gray-800">Genetic Correlation (LDSC)</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method1_ldsc.png" alt="LDSC Heatmap Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">On the homepage, click the <strong>Card 1</strong> (Genetic Correlation) in the Analysis Methods section, or click <strong>LDSC</strong> in the top navigation bar. This opens the interactive heatmap in a new tab.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">An interactive heatmap showing genetic correlation (rg) between all phenotype pairs. Color intensity represents the magnitude of correlation. Hover over cells to see exact values (rg, se, z, p).</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sample output</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left border-b">p1</th><th className="px-2 py-1 text-left border-b">p2</th><th className="px-2 py-1 text-left border-b">rg</th><th className="px-2 py-1 text-left border-b">se</th><th className="px-2 py-1 text-left border-b">p</th></tr></thead>
                    <tbody><tr><td className="px-2 py-1 border-b">Obesity</td><td className="px-2 py-1 border-b">Cardiovascular_diseases</td><td className="px-2 py-1 border-b">0.568</td><td className="px-2 py-1 border-b">0.027</td><td className="px-2 py-1 border-b">2.43e-100</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Method 2: Mendelian Randomization */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">2</span>
              <h3 className="text-lg font-bold text-gray-800">Mendelian Randomization (MR)</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method2_mr.svg" alt="MR Volcano Plot Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click <strong>Card 2</strong> (Mendelian Randomization) on the homepage, or click <strong>MR</strong> in the top navigation bar. This opens the MR results page in a new tab.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">A list of MR analyses for trait pairs. Each row shows the exposure trait, outcome trait, causal estimate (b), p-value, odds ratio (OR), and method used. Interactive volcano plots are also available.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sample output</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left border-b">exposure</th><th className="px-2 py-1 text-left border-b">outcome</th><th className="px-2 py-1 text-left border-b">method</th><th className="px-2 py-1 text-left border-b">nsnp</th><th className="px-2 py-1 text-left border-b">b</th><th className="px-2 py-1 text-left border-b">pval</th><th className="px-2 py-1 text-left border-b">or</th></tr></thead>
                    <tbody><tr><td className="px-2 py-1 border-b">Obesity</td><td className="px-2 py-1 border-b">Cardiovascular_diseases</td><td className="px-2 py-1 border-b">IVW</td><td className="px-2 py-1 border-b">140</td><td className="px-2 py-1 border-b">0.181</td><td className="px-2 py-1 border-b">1.62e-57</td><td className="px-2 py-1 border-b">1.200</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Method 3: Colocalization in LD Block */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">3</span>
              <h3 className="text-lg font-bold text-gray-800">Colocalization in LD Block</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method3_coloc.svg" alt="Coloc Visualization Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click <strong>Card 3</strong> (Colocalization in LD Block) on the homepage, or click <strong>Coloc</strong> in the top navigation bar. This opens the coloc visualization in a new tab.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">An interactive visualization showing coloc results across LD blocks. Each block displays posterior probabilities (PP.H0–H4) for shared causal variants between two traits.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sample output</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left border-b">nsnps</th><th className="px-2 py-1 text-left border-b">PP.H0.abf</th><th className="px-2 py-1 text-left border-b">PP.H1.abf</th><th className="px-2 py-1 text-left border-b">PP.H2.abf</th><th className="px-2 py-1 text-left border-b">PP.H3.abf</th><th className="px-2 py-1 text-left border-b">PP.H4.abf</th></tr></thead>
                    <tbody><tr><td className="px-2 py-1 border-b">245</td><td className="px-2 py-1 border-b">0.892</td><td className="px-2 py-1 border-b">0.097</td><td className="px-2 py-1 border-b">0.020</td><td className="px-2 py-1 border-b">1.64e-04</td><td className="px-2 py-1 border-b">0.009</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Method 4: SMR */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">4</span>
              <h3 className="text-lg font-bold text-gray-800">Summary Mendelian Randomization (SMR)</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method4_smr.svg" alt="SMR Volcano Plot Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click <strong>Card 4</strong> (SMR) on the homepage, or click <strong>SMR</strong> in the top navigation bar. This opens the SMR results page in a new tab.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">A volcano plot showing SMR results. X-axis: eQTL effect size (beta_SMR), Y-axis: -log10(p_SMR). Red dots indicate significant SMR signals.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sample output</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left border-b">Gene</th><th className="px-2 py-1 text-left border-b">Chr</th><th className="px-2 py-1 text-left border-b">Beta_SMR</th><th className="px-2 py-1 text-left border-b">P_SMR</th><th className="px-2 py-1 text-left border-b">P_HEIDI</th></tr></thead>
                    <tbody><tr><td className="px-2 py-1 border-b">CIB4</td><td className="px-2 py-1 border-b">2</td><td className="px-2 py-1 border-b">0.156</td><td className="px-2 py-1 border-b">3.21e-08</td><td className="px-2 py-1 border-b">0.234</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Method 5: Gene Causal */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">5</span>
              <h3 className="text-lg font-bold text-gray-800">Overall Causal Effect for Gene</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method5_gene.svg" alt="Gene Causal Spiral Plot Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click <strong>Card 5</strong> (Overall Causal Effect for Gene) on the homepage. This navigates to the Gene Causal page within the site.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">A gene search interface with an available gene list. Enter a gene symbol to view its spiral plot and MR results table against multiple disease outcomes.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Example input</p>
                <p className="text-sm text-gray-700">Type <code className="bg-gray-200 px-1 rounded text-xs">FIRRM</code> or <code className="bg-gray-200 px-1 rounded text-xs">A2M</code> in the search box, or click one of the available gene buttons. The spiral plot and MR results table will appear below.</p>
              </div>
            </div>
          </div>

          {/* Method 6: Phenotypic Co-occurrence */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">6</span>
              <h3 className="text-lg font-bold text-gray-800">Phenotypic Co-occurrence (HyPrColoc)</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method6_phenotypic.svg" alt="HyPrColoc Visualization Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click <strong>Card 6</strong> (Phenotypic Co-occurrence) on the homepage. This opens the HyPrColoc visualization in a new tab.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">An interactive visualization showing phenotypic co-occurrence patterns. Displays genes that show convergent evidence from both SMR and coloc analyses.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sample output</p>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left border-b">Gene</th><th className="px-2 py-1 text-left border-b">PPH4_coloc</th><th className="px-2 py-1 text-left border-b">Z_SMR</th><th className="px-2 py-1 text-left border-b">PP_INTACT</th></tr></thead>
                    <tbody><tr><td className="px-2 py-1 border-b">ABHD12</td><td className="px-2 py-1 border-b">0.613</td><td className="px-2 py-1 border-b">2.763</td><td className="px-2 py-1 border-b">0.547</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Method 7: BN-GWAS Network */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded bg-[#3b82f6] text-white text-xs font-bold flex items-center justify-center">7</span>
              <h3 className="text-lg font-bold text-gray-800">Disease Causal Network Identification (BN-GWAS)</h3>
            </div>
            <div className="ml-9 space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src="/Help_pic/method7_bngwas.svg" alt="BN-GWAS Network Example" className="w-full h-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">How to access</p>
                <p className="text-sm text-gray-700">Click the <strong>full-width Card 7</strong> (Disease Causal Network Identification) at the bottom of the Analysis Methods section on the homepage. This navigates to the BN-GWAS Network page.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">What you see</p>
                <p className="text-sm text-gray-700">A multi-tab page with Overview (network architecture diagram, statistics), Method (BN-GWAS workflow), and Example Network (searchable CRE-Gene-Phenotype associations).</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-1">Example input</p>
                <p className="text-sm text-gray-700">In the Example Network tab, type <code className="bg-gray-200 px-1 rounded text-xs">APOE</code> in the search box to find its CRE links (CRE_006), associated phenotypes (Alzheimers Disease, Lipid Levels), and causal score (92%).</p>
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

// Main App with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/network" element={<NetworkPage />} />
        <Route path="/gwas/:type" element={<GWASListPage />} />
        <Route path="/gene-causal" element={<GeneCausalPage />} />
        <Route path="/bngwas-network" element={<BNGWASNetworkPage />} />
        <Route path="/help" element={<HelpPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;